import Collection from './Collection.js'
import SlotsParser from './SlotsParser.js'

export default class {
  data = [] // local data
  disabledDates = [] // list of disabled dates - non selectable
  options = {
      'show-dates': false,
      'tooltip-table': {}
  }

  // map of classes for each slot type
  classes = {
      opening_hours: 'slot--opening-hours',
      availability_cancelled: 'slot--availability-cancelled',
      availability_new: 'slot--availability-new',
      consultations: 'slot--consultations',
      vacations: 'slot--vacations',
      holidays: 'slot--holidays'
  }

  mode = 'none'
  blacklist = {}
  whitelist = {}
  rules = {}

  /**
   * Returns local data as collection.
   *
   * @return {Collection}
   */
  get() {
      return new Collection(this.data)
  }

  /**
   * Assigns provided data after converting them for the frontend.
   *
   * @param {any} data
   */
  set(data) {
      this.data = this.convertForFrontend(data).all()
  }

  /**
   * Append provided data after converting them for the frontend.
   *
   * @param {any} data
   */
  append(data) {
      this.data = [...this.data, ...this.convertForFrontend(data).all()]

      this.reorder()
  }

  /**
   * Reorder data by a date.
   */
  reorder() {
      this.data = _.orderBy(this.data, 'date')
  }

  /**
   * Filters local data.
   *
   * @param callback
   */
  filter(callback) {
      this.data = _.filter(this.data, callback)
  }

  /**
   * Function to convert slots for backend.
   *
   * @param  {Array} data
   *
   * @return {Collection}
   */
  convertForBackend(data = null) {
      return new Collection(data || this.data).map((item) => {
          item.slots = _.flatten(item.slots.map((slot) => _.range(slot * 6 - 5, slot * 6 + 1)))
          return item
      })
  }

  /**
   * Function to convert slots for frontend.
   *
   * @param  {array} data
   *
   * @return {Collection}
   */
  convertForFrontend(data = null) {
      return new Collection(data || this.data).map((item) => {
          item.slots = Object.keys(_.groupBy(item.slots, (slot) => Math.ceil(slot / 6))).map((slot) => parseInt(slot))
          return item
      })
  }

  /**
   * Find a item by a query.
   *
   * @param  {object}      item
   * @param  {object|null} customQuery
   *
   * @return {object}
   */
  find(item, customQuery = null) {
      const query = customQuery || { type: item.type, date: item.date }

      return _.find(this.data, query)
  }

  /**
   * Add item.
   *
   * @param {[type]} newItem [description]
   */
  add(newItem) {
      if (!this.isValid(newItem)) {
          return
      }

      this.data.push(newItem)
  }

  /**
   * Toggle a slot.
   *
   * @param date
   * @param slot
   * @param type
   */
  toggle(date, slot, type = null) {
      type = type || this.mode

      const item = this.find({ date, type })

      if (this.isBlacklisted(date, slot, type)) {
          return
      }

      if (!this.isWhitelisted(date, slot, type)) {
          return
      }

      if (this.isDateDisabled(date)) {
          return
      }

      if (item) {
          const index = item.slots.indexOf(slot)
          let slots = _.clone(item.slots)

          if (index > -1) {
              slots = _.without(_.clone(slots), slot)
          } else {
              if (!this.isValid({ date, slot, type }, 'maxSlots')) {
                  return
              }

              slots = [...slots, slot]
          }

          this.update(item, { slots })
      } else {
          this.add({ date, slots: [slot], type })
      }
  }

  /**
   * Update item with provided data.
   *
   * @param item
   * @param data
   */
  update(item, data) {
      this.data = this.data.map((value) => {
          if (_.isEqual(item, value)) {
              value = { ...value, ...data }
              value.slots = _.uniq(value.slots)
          }

          return value
      })
  }

  /**
   * Set option.
   *
   * @param key
   * @param value
   */
  setOption(key, value) {
      this.options[key] = value
  }

  /**
   * Get option by a key.
   *
   * @param key
   * @returns {*}
   */
  option(key) {
      return this.options[key]
  }

  /**
   * Set mode.
   *
   * @param name
   */
  setMode(name) {
      this.mode = name
  }

  /**
   * Add a blacklist rule.
   *
   * @param type
   * @param blockedType
   */
  addToBlacklist(type, blockedType) {
      if (!this.blacklist[type]) {
          this.blacklist[type] = []
      }

      this.blacklist[type].push(blockedType)
  }

  /**
   * Add a whitelist rule.
   *
   * @param type
   * @param allowedType
   */
  addToWhitelist(type, allowedType) {
      if (!this.whitelist[type]) {
          this.whitelist[type] = []
      }

      this.whitelist[type].push(allowedType)
  }

  /**
   * Checks if slot by a specified type and date is blacklisted.
   *
   * @param date
   * @param slot
   * @param type
   * @returns {Boolean|boolean}
   */
  isBlacklisted(date, slot, type) {
      if (!this.blacklist[type]) {
          return false
      }

      return this.isSlotListed(this.blacklist[type], { date }, { type, slot })
  }

  /**
   * Checks if slot by a specified type and date is whitelisted.
   *
   * @param date
   * @param slot
   * @param type
   * @returns {Boolean|boolean}
   */
  isWhitelisted(date, slot, type) {
      if (!this.whitelist[type]) {
          return true
      }

      return this.isSlotListed(this.whitelist[type], { date }, { type, slot })
  }

  /**
   *
   * @param list
   * @param dataQuery
   * @param type
   * @param slot
   * @returns {boolean}
   */
  isSlotListed(list, dataQuery, { type, slot }) {
      const items = _.filter(this.data, dataQuery)

      for (const item of items) {
          if (item.type !== type && item.slots.indexOf(slot) > -1 && list.indexOf(item.type) > -1) {
              return true
          }
      }

      return false
  }

  /**
   * Adds a rule.
   *
   * @param type
   * @param rule
   */
  addRule(type, rule) {
      if (!this.rules[type]) {
          this.rules[type] = []
      }

      this.rules[type].push(rule)
  }

  /**
   * Checks if slot is available for selection.
   * So far only maxSlots rule is used.
   *
   * @param date
   * @param slot
   * @param type
   * @param rule
   * @returns {boolean}
   */
  isValid({ date, slot, type }, rule = null) {
      if (!this.rules[type]) {
          return true
      }

      const rules = rule ? this.rules[type].filter((v, k) => k !== rule) : this.rules[type]

      if (rules.length === 0) {
          console.warn('List of rules is empty.')
          return true
      }

      for (const rule of rules) {
          const [ruleName, ruleValue] = Object.entries(rule)[0]

          switch (ruleName) {
          case 'maxSlots':
              let maxSlotsCount = ruleValue

              // if ruleValue is a string, use it as a type and filter with it items and then count their slots
              if (_.isString(ruleValue)) {
                  const items = _.filter(this.data, { type: ruleValue })
                  maxSlotsCount = _.sum(_.map(items, (v) => v.slots.length))
              }

              const items = _.filter(this.data, { type })
              const currentCount = _.sum(_.map(items, (v) => v.slots.length))

              return currentCount < maxSlotsCount
              break

          default:
              return true
          }
      }
  }

  /**
   *
   * @param slots
   * @param slot
   * @returns {string}
   */
  getClasses(slots, slot) {
      const classes = []

      slots.forEach((item) => {
          if (item.slots.indexOf(slot) > -1) {
              classes.push(this.classes[item.type])
          }
      })

      return classes.join(' ')
  }

  /**
   *
   * @param slots
   * @param slot
   * @returns {[]}
   */
  getTypes(slots, slot) {
      const types = []

      for (const item of slots) {
          if (item.slots.indexOf(slot) > -1) {
              types.push(item.type)
          }
      }

      return types
  }

  /**
   *
   * @param type
   * @param className
   */
  addClass(type, className) {
      this.classes[type] = className
  }

  /**
   * Create slots parser instance.
   *
   * @return {[type]} [description]
   */
  slotsParser() {
      return SlotsParser.use(this.get())
  }

  /**
   * Returns local data as json.
   *
   * @returns {string}
   */
  toJson() {
      return this.get().toJson()
  }

  /**
   *
   * @param data
   */
  setDisabledDates(data) {
      this.disabledDates = data
  }

  /**
   *
   * @param date
   * @returns {boolean}
   */
  isDateDisabled(date) {
      return this.disabledDates.indexOf(date) > -1
  }
}
